import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppRouteService } from '@shared/services/app-route.service';
import { from, Observable, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthStoreService } from '../auth/auth-store.service';
import { ErrorStateService } from '../error/error-state-store.service';
import { ErrorAggregatorService } from '@root/app/error-aggregation/error-aggregator.service';
import { HttpErrorMessageHandlerService } from '@core/error/http-error-message-handler.service';
import { isHttpErrorDto } from '@core/error/errors.model';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly authStoreService = inject(AuthStoreService);
  private readonly errorStateService = inject(ErrorStateService);
  private readonly httpErrorMessageHandlerService = inject(
    HttpErrorMessageHandlerService
  );
  private readonly errorAggregatorService = inject(ErrorAggregatorService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          // Blob-typed requests (e.g. photo downloads) deliver the JSON error
          // body as a Blob, not a parsed object — unpack it first so the
          // handling logic and downstream consumers see a real HttpErrorDto.
          if (error.error instanceof Blob) {
            return from(this.parseBlobError(error)).pipe(
              switchMap((parsed) => this.handleHttpError(request, parsed))
            );
          }
          return this.handleHttpError(request, error);
        }

        return throwError(() => error);
      })
    );
  }

  private async parseBlobError(
    error: HttpErrorResponse
  ): Promise<HttpErrorResponse> {
    const blob = error.error as Blob;
    try {
      const text = await blob.text();
      const parsed: unknown = text ? JSON.parse(text) : null;
      // Only substitute the parsed body when it is a real error DTO, so
      // downstream consumers (getErrorMessage / error.error.reason) are
      // guaranteed the HttpErrorDto shape and a non-conforming body is not
      // dressed up as one.
      if (!isHttpErrorDto(parsed)) return error;
      return new HttpErrorResponse({
        error: parsed,
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined,
      });
    } catch {
      return error;
    }
  }

  private handleHttpError(
    request: HttpRequest<unknown>,
    error: HttpErrorResponse
  ): Observable<never> {
    this.httpErrorMessageHandlerService.handleGeneralHttpErrorMessage(error);
    this.errorAggregatorService.addHttpErrorToAggregationError(request, error);
    switch (error.status) {
      case HttpStatusCode.Unauthorized:
        this.authStoreService.logout();
        break;
      case HttpStatusCode.Forbidden:
        this.router.navigateByUrl(AppRouteService.forbiddenPath());
        break;
      case HttpStatusCode.HttpVersionNotSupported:
        this.errorStateService.setErrorState({
          state: 'versionNoSupported',
          message: error.error.reason,
        });
        break;
    }

    return throwError(() => error);
  }
}
